import { Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { AuthService, UserProfile } from '../../services/auth.service';
import { SessionManagerService } from '../../services/session-manager.service';
import { Observable, map, filter, startWith, shareReplay } from 'rxjs';
import { HomeDataService, HomeDataResponse } from '../../services/home-data.service';
import { GalleryService } from '../../services/gallery.service';
import { ToolsComponent } from '../tools/tools.component'
import { NavigationEnd } from '@angular/router';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, ToolsComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  isScrolled = false;
  isMenuOpen = false;
  isUserMenuOpen = false;
  isAdminMenuOpen = false;
  
  isAuthenticated$: Observable<boolean>;
  userProfile$: Observable<UserProfile | null>;
  isAdminVisible$: Observable<boolean>;
  isAdminRoute$: Observable<boolean>;
  homeData$: Observable<HomeDataResponse>;
  hasGalleries$: Observable<boolean>;
  hasTransparency$: Observable<boolean>;
  
  private currentUserProfile: UserProfile | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private sessionManager: SessionManagerService,
    private homeDataService: HomeDataService,
    private galleryService: GalleryService
  ) {
    this.homeData$ = this.homeDataService.getHomeData().pipe(shareReplay(1));
    this.isAuthenticated$ = this.authService.isAuthenticated$;
    this.userProfile$ = this.authService.userProfile$;
    this.isAdminVisible$ = this.authService.userProfile$.pipe(
      map(profile => {
        if (!profile) return false;
        return this.authService.hasAnyRole(['admin', 'editor', 'content.editor', 'finance.executive', 'legal.executive', 'project.executive']);
      })
    );
    
    this.isAdminRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.includes('/admin/')),
      startWith(this.router.url.includes('/admin/'))
    );

    this.hasGalleries$ = this.galleryService.getGalleries(true).pipe(
      map(galleries => galleries.length > 0),
      startWith(false),
      shareReplay(1)
    );

    this.hasTransparency$ = this.homeData$.pipe(
      map(data => !!data.transparency_categories && data.transparency_categories.filter(c => c.is_active).length > 0),
      startWith(false),
      shareReplay(1)
    );
    
    // Subscribe to user profile changes
    this.userProfile$.subscribe(profile => {
      this.currentUserProfile = profile;
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.pageYOffset > 50;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu')) {
      this.isUserMenuOpen = false;
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  toggleAdminMenu() {
    this.isAdminMenuOpen = !this.isAdminMenuOpen;
  }

  navigateToHome() {
    this.isMenuOpen = false;
    if (this.router.url === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.router.navigate(['/']).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  scrollToSection(sectionId: string) {
    this.isMenuOpen = false;
    
    // If not on home page, navigate to home first
    if (this.router.url !== '/') {
      this.router.navigate(['/']).then(() => {
        setTimeout(() => {
          this.scrollToElement(sectionId);
        }, 100);
      });
    } else {
      this.scrollToElement(sectionId);
    }
  }

  private scrollToElement(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Auth methods
  login() {
    this.authService.loginRedirect();
  }

  logout() {
    this.isUserMenuOpen = false;
    if (this.authService.isInteractionInProgress()) {
      console.warn('Interaction in progress, waiting before logout');
      this.authService.waitForInteractionToComplete().subscribe(() => {
        this.authService.logout();
      });
    } else {
      this.authService.logout();
    }
  }

  showUserProfile() {
    this.isUserMenuOpen = false;
    this.sessionManager.showUserProfile();
  }

  refreshToken() {
    this.isUserMenuOpen = false;
    
    if (this.authService.isInteractionInProgress()) {
      console.warn('Interaction already in progress, cannot refresh token now');
      return;
    }

    this.sessionManager.refreshSession().subscribe({
      next: () => {
        console.log('Token refreshed successfully');
      },
      error: (error) => {
        console.error('Error refreshing token:', error);
      }
    });
  }

  // Helper methods
  hasAdminAccess(): boolean {
    return this.authService.hasAnyRole(['admin', 'editor', 'content.editor', 'finance.executive', 'legal.executive', 'project.executive']);
  }

  getUserInitials(): string {
    if (!this.currentUserProfile?.displayName) return '??';
    const parts = this.currentUserProfile.displayName.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return this.currentUserProfile.displayName.substring(0, 2).toUpperCase();
  }

  getUserDisplayName(): string {
    return this.currentUserProfile?.displayName || 'Usuario';
  }

  getUserEmail(): string {
    return this.currentUserProfile?.mail || '';
  }
}